import LetrusLoader from 'components/LetrusLoader';
import {Button, CircularProgressbar, Modal} from 'letrus-ui';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  fetchCompositionJsonStatsAsyncRequest,
  getCompositionJsonStatsAsync,
  isLoadingCompositionsJsonStatsAsync,
} from 'store/reducers/compositions';
import {ApplicationState} from 'store/rootReducer';
import {useMobile} from 'utils/hooks';
import {CompositionDetailExtended} from 'utils/types/compositions';
import styles from './ILetrusModal.module.scss';

interface DispatchProps {
  fetchCompositionJsonStatsAsyncRequest: typeof fetchCompositionJsonStatsAsyncRequest;
}

interface StateProps {
  jsonStatsAsync: ImmutableMap<any>;
  isLoadingCompositionsJsonStatsAsync: boolean;
}

interface OwnProps {
  id: string;
  onClick(): void;
  iLetrusType?: 'iletrus' | 'iletrus_delta';
  finishedTimer: boolean;
  userText?: string;
  composition: ImmutableMap<CompositionDetailExtended>;
}

type ILetrusModalProps = OwnProps & StateProps & DispatchProps;

interface ILetrus {
  score: number;
  title?: string;
  body?: JSX.Element;
  fetch?: boolean;
}

function ILetrusModal({
  id,
  onClick,
  userText,
  composition,
  finishedTimer,
  iLetrusType,
  jsonStatsAsync,
  isLoadingCompositionsJsonStatsAsync,
  fetchCompositionJsonStatsAsyncRequest,
}: ILetrusModalProps): JSX.Element {
  const [iLetrus, setILetrus] = useState<ILetrus>({score: 0.0});
  const {isMobile} = useMobile();
  const parentId = composition.get('parent_id');

  useEffect(() => {
    fetchCompositionJsonStatsAsyncRequest(composition.toJS(), {
      compositionRaw: userText ?? '',
      markersToProcess: (
        composition.getIn(['genre', 'shown_stats'])?.toJS() ?? []
      ).map((shownStat: any) => shownStat?.marker?.varname),
      setMistakesHighlight: true,
      fromMobile: isMobile,
    });
  }, []);

  useEffect(() => {
    if (!isLoadingCompositionsJsonStatsAsync && jsonStatsAsync.size) {
      updateILetrusScore();
    }
  }, [isLoadingCompositionsJsonStatsAsync]);

  function updateILetrusScore() {
    if (
      (!iLetrus?.fetch || parentId) &&
      typeof jsonStatsAsync.get('jsonMetadata') !== 'undefined'
    ) {
      const iLetrusScore = jsonStatsAsync.getIn([
        'jsonMetadata',
        'letrus_score',
        'total_score',
      ]);

      const iLetrusScoreDiff = Math.round(iLetrusScore * 100);
      const isIletrusDelta = iLetrusType === 'iletrus_delta';

      let iLetrusScoreTitle = '';
      let iLetrusScoreBody = <></>;

      if (finishedTimer) {
        iLetrusScoreTitle = 'Opa, seu tempo acabou!';

        if (isIletrusDelta) {
          if (iLetrusScoreDiff > 0) {
            iLetrusScoreBody = (
              <p>
                Mas não se preocupe, esta primeira atividade não vale nota.
                <br />
                <b>{`Seu texto evoluiu ${iLetrusScoreDiff}%!!!`}</b>
                {` Sua estrutura alcançou ${Math.round(
                  iLetrusScore * 100,
                )}%! Uma ótima progressão para o seu primeiro texto com a gente.`}
              </p>
            );
          } else {
            iLetrusScoreBody = (
              <p className={styles.iLetrusScoreBody}>
                Você não conseguiu melhorar o seu texto. Tudo bem, esta foi a
                sua primeira escrita com a gente. Você fará um novo texto na
                Letrus em breve e poderá evoluir bastante até o final do ano.
              </p>
            );
          }
        } else {
          iLetrusScoreBody = (
            <p>
              {`Mas não se preocupe, você poderá continuar escrevendo na próxima
              etapa. Analisamos a estrutura do seu texto e ele tem ${Math.round(
                iLetrusScore * 100,
              )}% da qualidade esperada para uma redação nota mil!`}
            </p>
          );
        }
      }

      if (!finishedTimer) {
        if (isIletrusDelta) {
          if (iLetrusScoreDiff > 0) {
            iLetrusScoreTitle = 'SHOW!!!';
            iLetrusScoreBody = (
              <p>
                <b>{`Seu texto evoluiu ${iLetrusScoreDiff}%!!! Parabéns!`}</b>
                <br />
                {` Sua estrutura alcançou ${Math.round(
                  iLetrusScore * 100,
                )}%! Uma ótima progressão para o seu primeiro texto com a gente.`}
              </p>
            );
          } else {
            iLetrusScoreTitle = '';
            iLetrusScoreBody = (
              <p className={styles.iLetrusScoreBody}>
                Você não conseguiu melhorar o seu texto. Tudo bem, esta foi a
                sua primeira escrita com a gente. Você fará um novo texto na
                Letrus em breve e poderá evoluir bastante até o final do ano.
              </p>
            );
          }
        } else {
          iLetrusScoreTitle = 'BOOOOAAA!';
          iLetrusScoreBody = (
            <p>
              {`Analisamos a estrutura do seu texto e ele tem ${Math.round(
                iLetrusScore * 100,
              )}% da qualidade esperada para uma redação nota mil!`}
            </p>
          );
        }
      }

      setILetrus({
        score: iLetrusScore,
        title: iLetrusScoreTitle,
        body: iLetrusScoreBody,
        fetch: true,
      });
    }
  }

  return (
    <Modal id={id}>
      {isLoadingCompositionsJsonStatsAsync ? (
        <LetrusLoader />
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.circularProgressBar}>
            <CircularProgressbar
              score={Math.round(iLetrus?.score * 100)}
              size={120}
              showInfoIcon={false}
            />
          </div>

          <p>
            <strong>{iLetrus?.title}</strong>
          </p>
          {iLetrus?.body}
          {!parentId && (
            <p>
              <strong>Bora melhorar?</strong>
            </p>
          )}
          <div className="buttonRow">
            <Button onClick={onClick}>
              {!parentId ? 'Melhorar meu texto' : 'Próxima etapa'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default connect(
  (state: ApplicationState) => ({
    jsonStatsAsync: getCompositionJsonStatsAsync(state),
    isLoadingCompositionsJsonStatsAsync:
      isLoadingCompositionsJsonStatsAsync(state),
  }),
  {
    fetchCompositionJsonStatsAsyncRequest,
  },
)(ILetrusModal);
